import React, {FC, useState} from 'react'
import {CommonUtils, DateTimeUtils} from '../../../../../utils'
import ListItemAction from '../../../../../components/Actions/ListItemAction'
import ViewAction from '../../../../../components/Actions/ViewAction'
import AntTable from '../../../../../components/Table/AntTable'
import {DeviceAction} from '../Actions/Device.actions'
import {Button} from 'react-bootstrap'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import {Modal} from 'antd'
import {GoogleMap, LoadScript, Marker} from '@react-google-maps/api'

dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)
interface LocationModalProps {
  isOpen: boolean
  onClose: () => void
  coordinates: string
  equipmentName: string
}

const LocationModal: FC<LocationModalProps> = ({isOpen, onClose, coordinates, equipmentName}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  // Parse coordinates (assuming format "lat,lng") with validation
  const coordArray = coordinates
    ? coordinates.split(',').map((coord) => parseFloat(coord.trim()))
    : [0, 0]
  const lat = isNaN(coordArray[0]) ? 0 : coordArray[0]
  const lng = isNaN(coordArray[1]) ? 0 : coordArray[1]

  const isValidCoordinates = lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
  }

  const center = {
    lat: lat,
    lng: lng,
  }

  const mapOptions = {
    zoom: isValidCoordinates ? 15 : 2,
    center: center,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
  }

  return (
    <Modal
      title={`Location - ${equipmentName}`}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
    >
      <div style={{height: '400px'}}>
        {apiKey && isValidCoordinates ? (
          <LoadScript
            googleMapsApiKey={apiKey}
            loadingElement={
              <div className='d-flex justify-content-center align-items-center h-100'>
                <div className='text-center'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  <p className='mt-2 text-muted'>Loading map...</p>
                </div>
              </div>
            }
          >
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={15}
              options={mapOptions}
            >
              <Marker position={center} title={`${equipmentName} Location`} />
            </GoogleMap>
          </LoadScript>
        ) : (
          <div className='d-flex justify-content-center align-items-center h-100 bg-light'>
            <div className='text-center'>
              <i className='bi bi-geo-alt-fill fs-1 text-muted mb-3'></i>
              {!apiKey ? (
                <>
                  <h5 className='text-muted'>Google Maps API Key not configured</h5>
                  <p className='text-muted'>Coordinates: {coordinates || 'N/A'}</p>
                </>
              ) : !isValidCoordinates ? (
                <>
                  <h5 className='text-muted'>Invalid coordinates</h5>
                  <p className='text-muted'>Coordinates: {coordinates || 'N/A'}</p>
                </>
              ) : null}
              {coordinates && (
                <a
                  href={`https://maps.google.com/?q=${coordinates}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='btn btn-primary'
                >
                  <i className='bi bi-geo-alt-fill me-1'></i>
                  Open in Google Maps
                </a>
              )}
            </div>
          </div>
        )}
      </div>
      <div className='mt-3 text-center'>
        <small className='text-muted'>
          Coordinates:{' '}
          {isValidCoordinates ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : coordinates || 'N/A'}
        </small>
      </div>
    </Modal>
  )
}

const DeviceListing: FC<any> = (props) => {
  const {
    loading,
    listData,
    selectedRowKeys,
    onChangeSwitchToggle,
    handleOnChanged,
    handleTableChange,
    handleCallbackFunc,
    pagination,
  } = props

  // Modal state
  const [locationModal, setLocationModal] = useState<{
    isOpen: boolean
    coordinates: string
    equipmentName: string
  }>({
    isOpen: false,
    coordinates: '',
    equipmentName: '',
  })

  let showingStart = (pagination?.currentPage - 1) * pagination?.pageSize + 1

  const handleLocationClick = (coordinates: string, equipmentName: string) => {
    setLocationModal({
      isOpen: true,
      coordinates,
      equipmentName,
    })
  }

  const closeLocationModal = () => {
    setLocationModal({
      isOpen: false,
      coordinates: '',
      equipmentName: '',
    })
  }

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Device Name',
      sorter: true,
      width: '10%',
      // render: (text: string, record: any, index: number) => <ViewAction entityId={record.id} actionItem={DeviceAction.COMMON_ACTION.VIEW} defaultViewText={text} handleCallbackFunc={handleCallbackFunc}><span className="grid-row-view-action cursor-pointer fw-bolder">{text}</span></ViewAction>,
    },
    {
      dataIndex: 'hardware_id',
      key: 'hardware_id',
      title: 'Hardware ID',
      sorter: false,
      width: '10%',
      // render: (text: string, record: any, index: number) => <ViewAction entityId={record.id} actionItem={DeviceAction.COMMON_ACTION.VIEW} defaultViewText={text} handleCallbackFunc={handleCallbackFunc}><span className="grid-row-view-action cursor-pointer fw-bolder">{text}</span></ViewAction>,
    },
    {
      dataIndex: 'mobile_number',
      key: 'mobile_number',
      title: 'Mobile',
      sorter: false,
      width: '15%',
      // render: (text: string, record: any, index: number) => <ViewAction entityId={record.id} actionItem={DeviceAction.COMMON_ACTION.VIEW} defaultViewText={text} handleCallbackFunc={handleCallbackFunc}><span className="grid-row-view-action cursor-pointer fw-bolder">{text}</span></ViewAction>,
    },
    {
      dataIndex: 'organization_name',
      key: 'organization_name',
      title: 'Organization',
      sorter: false,
      width: '5%',
    },
    {
      dataIndex: 'organogram_name_en',
      key: 'organogram_name_en',
      title: 'Organogram',
      sorter: false,
      width: '15%',
    },
    {
      dataIndex: 'time',
      key: 'time',
      title: 'Differance',
      sorter: false,
      width: '5%',
      render: (text: number, record: any, index: number) => {
        const currentTime = dayjs()
        const updatedTime = record.latest_stream_data
          ? dayjs(record.latest_stream_data.updated_at)
          : null

        if (!updatedTime) return ''

        const timeDiff = currentTime.diff(updatedTime, 'day')

        let color = ''
        if (timeDiff > 30) {
          color = 'red'
        } else if (timeDiff > 7) {
          color = 'orange'
        } else if (timeDiff > 2) {
          color = 'blue'
        }

        return <span style={{color}}>{updatedTime.from(currentTime)}</span>
      },
    },
    {
      dataIndex: 'updated_at',
      key: 'updated_at',
      title: 'Updated At',
      sorter: true,
      width: '10%',
      render: (text: number, record: any, index: number) =>
        record.latest_stream_data ? record.latest_stream_data.updated_at : '',
    },
    {
      dataIndex: 'lat',
      key: 'lat',
      title: 'Data',
      width: '40%',
      render: (text: number, record: any, index: number) => (
        <div className='d-flex flex-wrap mb-0'>
          {record.latest_stream_data?.details
            ?.filter((item: any) => item.datastream !== 'lat_lng')
            .map((item: any, id: number) => (
              <div key={id} className='border border-gray-300 rounded p-2 me-3 mb-3'>
                <div
                  className={`fs-6 fw-bolder text-gray-800`}
                  data-bs-toggle='tooltip'
                  data-bs-placement='top'
                  title={`Threshold: Low ${item.threshold_low ?? 'N/A'}, High ${
                    item.threshold_high ?? 'N/A'
                  }`}
                >
                  {item.parameter_name}
                </div>
                <div
                  className={`fw-bold fs-4 ${
                    item.threshold_low != null &&
                    item.threshold_high != null &&
                    (item.datastream_value < item.threshold_low ||
                      item.datastream_value > item.threshold_high)
                      ? 'text-danger'
                      : 'text-dark'
                  }`}
                >
                  {(() => {
                    const paramShortName = item.datastream?.toLowerCase()
                    const isOnline = record.latest_stream_data_status === "Online";
                    const value = isOnline ? item.datastream_value : "---";
                    let iconClass = 'bi bi-question-circle'
                    let isActive = value == 1

                    if (paramShortName?.startsWith('t')) {
                      iconClass = 'bi bi-thermometer-half'
                    } else if (
                      paramShortName?.startsWith('r') ||
                      paramShortName?.includes('state')
                    ) {
                      iconClass = isActive ? 'bi bi-plug text-success' : 'bi bi-plug text-secondary'
                    } else if (paramShortName?.includes('comp')) {
                      iconClass = isActive ? 'bi bi-fan text-success' : 'bi bi-fan text-secondary'
                    } else if (paramShortName?.startsWith('di')) {
                      iconClass = isActive
                        ? 'bi bi-circle-fill text-success'
                        : 'bi bi-circle text-secondary'
                    } else if (paramShortName?.includes('rpm')) {
                      iconClass = 'bi bi-speedometer2'
                    } else if (paramShortName?.includes('hum') || paramShortName?.includes('rh')) {
                      iconClass = 'bi bi-droplet-half'
                    } else if (
                      paramShortName?.includes('press') ||
                      paramShortName?.includes('psi')
                    ) {
                      iconClass = 'bi bi-speedometer'
                    } else if (paramShortName?.includes('volt') || paramShortName?.includes('v')) {
                      iconClass = 'bi bi-lightning-charge'
                    } else if (
                      paramShortName?.includes('amp') ||
                      paramShortName?.includes('current')
                    ) {
                      iconClass = 'bi bi-lightning'
                    } else if (paramShortName?.includes('flow')) {
                      iconClass = 'bi bi-water'
                    } else if (paramShortName?.includes('dir')) {
                      iconClass = 'bi bi-signpost-split'
                    } else if (paramShortName?.includes('ignition')) {
                      iconClass =
                        isActive && item.datastream_value == 1
                          ? 'bi bi-car-front-fill text-success'
                          : 'bi bi-car-front text-secondary'
                    }

                    return (
                      <>
                        <i
                          className={`${iconClass} fs-1 me-2`}
                          data-bs-toggle='tooltip'
                          data-bs-placement='top'
                          title={`${item.parameter_name} Threshold: Low ${
                            item.threshold_low ?? 'N/A'
                          }, High ${item.threshold_high ?? 'N/A'}`}
                        ></i>
                      </>
                    )
                  })()}

                  {item.datastream_value_masking
                    ? item.datastream_value_masking + ' ' + item.unit_short_name
                    : ''}
                </div>
              </div>
            ))}

          {record.latest_stream_data?.details
            ?.filter((item: any) => item.datastream === 'lat_lng')
            .map((item: any, id: number) => (
              <div key={`latlng-${id}`} className='border border-gray-300 rounded p-2 me-3 mb-3'>
                <div className='fs-6 fw-bolder text-gray-800'>Location</div>
                <div className='fw-bold text-gray-400'>
                  <button
                    onClick={() => handleLocationClick(item.datastream_value, record.name)}
                    className='btn btn-link text-decoration-none text-gray-400 p-0'
                    style={{border: 'none', background: 'none'}}
                  >
                    <i className='bi bi-geo-alt-fill me-1'></i>
                    View Location
                  </button>
                </div>
              </div>
            ))}
        </div>
      ),
    },
  ]

  return (
    <div className='px-6'>
      <AntTable
        className='table-layout'
        rowSelection={true}
        rowSelectionPermission='auth:example:multiSelect'
        selectedRowKeys={selectedRowKeys}
        dataSource={listData}
        columns={columns}
        loading={loading}
        handleOnChanged={handleOnChanged}
        onChange={handleTableChange}
      />
    </div>
  )
}

export default React.memo(DeviceListing)
